import * as React from "react";
import { useQuery } from "@tanstack/react-query";
import { PostServices } from "@/services";
import { NeedsAttentionCard } from "./components/NeedsAttentionCard";
import { Skeleton } from "@/components/ui";
import { TriangleAlert } from "lucide-react";

type Props = {
  playerId: number;
};

const NeedsAttentionFC: React.FC<Props> = ({ playerId }) => {
  const {
    data: postsData,
    isLoading: loading,
    refetch: getInfo,
  } = useQuery({
    queryKey: ["needsAttentionPosts", playerId],
    queryFn: async () => {
      const response = await PostServices.needAttentionPost(playerId);
      return response.content || [];
    },
    enabled: !!playerId,
  });

  const posts = postsData || [];

  return (
    <div className="needsAttentionContent w-full">
      <div className="w-full flex flex-col gap-4">
        {loading ? (
          <div className="needsAttention flex flex-col gap-3 w-full">
            {/* Header label & icon skeletons */}
            <div className="flex flex-row items-center gap-2">
              <Skeleton className="h-8 w-60" />
              <Skeleton className="size-6 rounded-full shrink-0" />
            </div>
            {/* Horizontal scroll skeletons matching card structure exactly */}
            <div className="flex flex-row overflow-x-auto gap-4 w-full pb-3 pr-2 scrollbar-thin scrollbar-thumb-slate-200 select-none">
              {Array.from({ length: 4 }).map((_, idx) => (
                <div
                  key={`skeleton-${idx}`}
                  style={{ width: "220px", height: "220px" }}
                  className="bg-white rounded border border-slate-100 flex flex-col justify-between overflow-hidden animate-pulse shrink-0 p-0"
                >
                  <Skeleton className="h-[95px] w-full rounded-t" />
                  <div className="p-2.5 pt-2 flex flex-col justify-between flex-1 gap-1">
                    <div className="flex flex-col gap-1">
                      <Skeleton className="h-3.5 w-40" />
                      <Skeleton className="h-3.5 w-32" />
                    </div>
                    <Skeleton className="h-3 w-20" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : posts && posts.length > 0 ? (
          <div className="needsAttention flex flex-col gap-3 w-full">
            <div className="flex flex-row items-center gap-2">
              <div className="text-2xl font-bold text-slate-700 leading-snug">Needs Your Attention</div>
              <TriangleAlert className="size-6 text-red-500 shrink-0" />
            </div>
            {/* Horizontally scrolling list container with tight spacing */}
            <div className="flex flex-row overflow-x-auto gap-4 w-full pb-3 scrollbar-thin scrollbar-thumb-slate-200">
              {posts.map((post, index) => (
                <NeedsAttentionCard key={index} post={post} refreshHandler={getInfo} />
              ))}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export const NeedsAttention = NeedsAttentionFC;
